// eslint-disable-next-line no-restricted-imports
import {
  Accordion,
  Box,
  Button,
  Card,
  Dialog,
  Divider,
  Flex,
  ProgressIndicator,
  Select,
  Text,
  TextInput,
  createForm,
  useToasts,
} from '@applyboard/crystal-ui';
import { DocumentIcon } from '@applyboard/ui-icons';
import { useTranslation } from '@applyboard/ui-utils';
import { ToastInterface } from '../../../common/schema/forms';
import { usePermissions } from '../../../data/permissions';
import {
  GicApplicationError,
  useGicApplicationInternalUpdate,
} from '../../../data/useGicApplicationInternalUpdate';
import { fetchSupportingDocument } from '../../../data/useGicApplications';
import { GIC_STATUS_OPTIONS_LIST } from './constants';
import { EditGicFormFieldValues } from './types';

export const handleButtonClick = async (
  externalId: string,
  toasts: ToastInterface,
  supportingDocumentType = 'loa',
) => {
  if (externalId) {
    try {
      const response = await fetchSupportingDocument(externalId, supportingDocumentType);
      window.open(response.data.attributes.presigned_url, '_blank');
    } catch (error) {
      const errorMessage = error as Error;
      toasts.negative(errorMessage);
    }
  } else {
    toasts.negative(new Error('External ID is not defined'));
  }
};

const { Form: GicForm, Field: GicField } = createForm<EditGicFormFieldValues>();

export const EditGicForm = ({
  data,
  onClose,
}: {
  data: EditGicFormFieldValues;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const toasts = useToasts();
  const { isLoadingPermissions, resourceToPermissionsMapping } =
    usePermissions('financial_products');
  const { gicApplicationInternalUpdate, isProcessingGicApplicationInternalUpdate } =
    useGicApplicationInternalUpdate();

  const hasGicWritePermission =
    resourceToPermissionsMapping?.financial_products?.includes('gic_application_write');

  if (isLoadingPermissions) {
    return (
      <Box px={20}>
        <Flex align="center" justify="center" pt={4}>
          <ProgressIndicator size="lg" label="Loading..." variant="circular" indeterminate />
        </Flex>
      </Box>
    );
  }

  return (
    <GicForm
      id="editGicForm"
      defaultValues={data}
      onSubmit={async values => {
        gicApplicationInternalUpdate(
          values.externalId,
          { status: values.status, recruitment_partner_id: values.rpId },
          () => {
            toasts.positive('Record updated successfully');
          },
          error => {
            console.error('Error signing up for rewards:', error);
            let errorMessage = t('common.errors.submission');

            const gicApplicationError = error as GicApplicationError;
            errorMessage =
              gicApplicationError.errorBody.message instanceof Array
                ? gicApplicationError.errorBody.message.join(',')
                : gicApplicationError.errorBody.message;

            toasts.negative(new Error(errorMessage));
          },
        );
      }}
      onError={error => alert(JSON.stringify(error))}
    >
      <Dialog open onOpenChange={onClose}>
        <Dialog.Heading>{t('fintech.gicEditHeadings.topHeading')}</Dialog.Heading>
        <Dialog.Content>
          <Flex direction="column" align="start" gap={4}>
            <GicField as={TextInput} name="rpId" label="RP ID" disabled={!hasGicWritePermission} />
            <GicField
              as={Select}
              name="status"
              label="Status"
              appearance="native"
              disabled={!hasGicWritePermission}
            >
              {GIC_STATUS_OPTIONS_LIST.map(opt => (
                <Select.Option {...opt} key={opt.value} />
              ))}
            </GicField>
            <div style={{ width: '100%' }}>
              <Flex justify="start" gap={2} direction="column">
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.letterOfAcceptance')}
                </Text>
                <div style={{ width: '100%' }}>
                  <Card emphasis="highlighted">
                    <Flex justify="between" gap={4}>
                      <Flex justify="start" align="center" gap={4}>
                        <DocumentIcon variant="outline" />
                      </Flex>
                      <Flex justify="end" gap={4}>
                        <Button
                          intent="primary"
                          onClick={() => handleButtonClick(data.externalId, toasts)}
                        >
                          {t('fintech.gicEditHeadings.viewButton')}
                        </Button>
                        <Button
                          intent="primary"
                          onClick={() => {
                            toasts.negative(
                              new Error(
                                'In Progress! This should be implemented as part of FTCE-1169',
                              ),
                            );
                          }}
                        >
                          {t('fintech.gicEditHeadings.replaceButton')}
                        </Button>
                      </Flex>
                    </Flex>
                  </Card>
                </div>
              </Flex>
            </div>
            <div style={{ width: '100%' }}>
              <Flex justify="start" gap={2} direction="column">
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.applicationPackage')}
                </Text>
                <div style={{ width: '100%' }}>
                  <Card emphasis="highlighted">
                    <Flex justify="between" gap={4}>
                      <Flex justify="start" align="center" gap={4}>
                        <DocumentIcon variant="outline" />
                      </Flex>
                      <Flex justify="end" gap={4}>
                        <Button
                          intent="primary"
                          onClick={() => handleButtonClick(data.externalId, toasts, 'package')}
                        >
                          {t('fintech.gicEditHeadings.viewButton')}
                        </Button>
                      </Flex>
                    </Flex>
                  </Card>
                </div>
              </Flex>
            </div>
          </Flex>
          <Divider direction="horizontal" />
          <Accordion>
            <Accordion.Item id="1" title="Additional GIC Information">
              <Flex justify="between" gap={4}>
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.Id')}
                </Text>
                <Text variant="bodyM" align="end">
                  {data.externalId}
                </Text>
              </Flex>
              <Divider direction="horizontal" />
              <Flex justify="between" gap={4}>
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.gicId')}
                </Text>
                <Text variant="bodyM" align="end">
                  {data.gicId}
                </Text>
              </Flex>
              <Divider direction="horizontal" />
              <Flex justify="between" gap={4}>
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.status')}
                </Text>
                <Text variant="bodyM" align="end">
                  {data.status}
                </Text>
              </Flex>
              <Divider direction="horizontal" />
              <Flex justify="between" gap={4}>
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.lastUpdatedOn')}
                </Text>
                <Text variant="bodyM" align="end">
                  {data.lastUpdatedOn}
                </Text>
              </Flex>
              <Divider direction="horizontal" />
              <Flex justify="between" gap={4}>
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.studentName')}
                </Text>
                <Text variant="bodyM" align="end">
                  {data.studentName}
                </Text>
              </Flex>
              <Divider direction="horizontal" />
              <Flex justify="between" gap={4}>
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.studentId')}
                </Text>
                <Text variant="bodyM" align="end">
                  {data.studentId}
                </Text>
              </Flex>
              <Divider direction="horizontal" />
              <Flex justify="between" gap={4}>
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.studentDateOfBirth')}
                </Text>
                <Text variant="bodyM" align="end">
                  {data.studentDateOfBirth}
                </Text>
              </Flex>
              <Divider direction="horizontal" />
              <Flex justify="between" gap={4}>
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.email')}
                </Text>
                <Text variant="bodyM" align="end">
                  {data.email}
                </Text>
              </Flex>
              <Divider direction="horizontal" />
              <Flex justify="between" gap={4}>
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.createdDate')}
                </Text>
                <Text variant="bodyM" align="end">
                  {data.createdDate}
                </Text>
              </Flex>
              <Divider direction="horizontal" />
              <Flex justify="between" gap={4}>
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.studentNationality')}
                </Text>
                <Text variant="bodyM" align="end">
                  {data.studentNationality}
                </Text>
              </Flex>
              <Divider direction="horizontal" />
              <Flex justify="between" gap={4}>
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.destinationProvince')}
                </Text>
                <Text variant="bodyM" align="end">
                  {data.destinationProvince}
                </Text>
              </Flex>
              <Divider direction="horizontal" />
              <Flex justify="between" gap={4}>
                <Text variant="labelM" align="start">
                  {t('fintech.gicEditHeadings.school')}
                </Text>
                <Text variant="bodyM" align="end">
                  {data.schoolName}
                </Text>
              </Flex>
            </Accordion.Item>
          </Accordion>
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.CloseButton width="fill">{t('common.cancel')}</Dialog.CloseButton>
          <Button
            intent="primary"
            width="fill"
            type="submit"
            form="editGicForm"
            disabled={isProcessingGicApplicationInternalUpdate || !hasGicWritePermission}
          >
            {t('common.submit')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </GicForm>
  );
};
